/**
 * Mode persistence utilities for managing Turbo vs Normal mode
 *
 * Priority order for mode detection:
 * 1. URL parameter: ?mode=turbo (highest priority)
 * 2. localStorage: hailmary.mode (persisted state)
 * 3. Default: 'normal'
 *
 * When URL param is present, it updates localStorage for persistence.
 */

const STORAGE_KEY = 'hailmary.mode';

export type AppMode = 'normal' | 'turbo';

/**
 * Get the current mode from URL params or localStorage
 * URL param takes precedence and also updates localStorage
 * Defaults to 'normal' if not set
 */
export function getMode(): AppMode {
  // First check URL param (takes precedence)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const urlMode = urlParams.get('mode');
    if (urlMode === 'turbo' || urlMode === 'normal') {
      // Persist to localStorage when set via URL
      setMode(urlMode);
      return urlMode;
    }
  }

  // Fall back to localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'turbo' || stored === 'normal') {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read mode from localStorage:', error);
  }
  return 'normal';
}

/**
 * Check if currently in turbo mode
 * Convenience function for quick checks
 */
export function isTurboMode(): boolean {
  return getMode() === 'turbo';
}

/**
 * Set the current mode in localStorage
 */
export function setMode(mode: AppMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (error) {
    console.warn('Failed to save mode to localStorage:', error);
  }
}

/**
 * Clear the stored mode (returns to default 'normal')
 */
export function clearMode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear mode from localStorage:', error);
  }
}

/**
 * Check if debug features should be enabled
 * Returns true if any of:
 * - URL has ?debug=1
 * - Mode is turbo (turbo is the internal/fast path)
 * - Environment is not production
 */
export function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  // Check URL param
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('debug') === '1') {
    return true;
  }

  // Check turbo mode (turbo users are power users who want debug access)
  if (isTurboMode()) {
    return true;
  }

  // Check environment (dev builds always have debug)
  const env = import.meta.env?.MODE;
  if (env && env !== 'production') {
    return true;
  }

  return false;
}
